import { useMemo } from 'react'
import { FreeMode, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { cn } from '@/lib/utils'

import { mapGraphQLProductToProduct, Skeleton, useWindowSize } from '../../../../Theme'
import { ProductCard, SectionTitle } from '../../../../UiBlocks'
import { LABELS, findCustomAttributeValue } from '../../../lib'
import { useRelatedProducts } from '../../../state'
import { ProductCardActions } from '../../ProductCardActions'

import type { AutoRelatedProduct } from '../../../types'

// Swiper styles are imported globally in styles/globals.css

export function AutoRelated() {
  const { autoRelatedProducts, loading } = useRelatedProducts()
  const { isMobile, isTablet } = useWindowSize()

  const itemsToShow = isMobile ? 1 : 4

  const availableProducts = useMemo((): AutoRelatedProduct[] => {
    if (!autoRelatedProducts) return []
    return autoRelatedProducts.filter((product) => {
      const isInStock = findCustomAttributeValue(product.custom_attributes, 'is_in_stock')
      return isInStock !== 'Out of Stock'
    })
  }, [autoRelatedProducts])

  if (loading) {
    return (
      <div className="relative react-css mt-5">
        <Skeleton className="h-[50px] w-48 ml-5 xl:ml-10 mb-4" />
        <div className="flex gap-2 px-1">
          {Array.from({ length: isMobile ? 1 : 4 }).map((_, i) => (
            <div key={i} className="flex-1 p-2">
              <Skeleton className="aspect-square w-full mb-2" />
              <Skeleton className="h-4 w-3/4 mb-1" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!availableProducts.length) return null

  const isOverflowing = availableProducts.length > itemsToShow
  let marginLeft: string
  if (isMobile) {
    marginLeft = isOverflowing ? 'ml-[18%]' : 'ml-[0]'
  } else if (isTablet) {
    marginLeft = isOverflowing ? 'ml-[10%]' : 'ml-[0]'
  } else {
    marginLeft = isOverflowing ? 'ml-[5%]' : 'ml-[0]'
  }

  return (
    <div className="relative react-css mt-5">
      <SectionTitle
        size="base"
        align="left"
        className={cn("absolute leading-[40px] xl:leading-[50px] xl:px-10 xl:w-full z-[2] pl-5 mb-0", marginLeft)}
        as="h2"
      >
        {LABELS.relatedProducts}
      </SectionTitle>
      <Swiper
        navigation={true}
        modules={[Navigation, FreeMode]}
        slidesPerView={itemsToShow}
        spaceBetween={0}
        freeMode={true}
        className="react-slider"
      >
        {availableProducts.map((product) => {
          const mappedProduct = mapGraphQLProductToProduct(product)
          return (
            <SwiperSlide key={mappedProduct.id} className="px-1 py-2 !w-full md:!w-[24.8%]">
              <ProductCard product={mappedProduct} renderActions={(p) => <ProductCardActions product={p} />} />
            </SwiperSlide>
          )
        })}
      </Swiper>
    </div>
  )
}
